// The download area is laid out by SPTK version on disk, and every release directory holds the
// XMQ packages built with that SPTK version. The Downloads page turns that inside out: XMQ is
// what is downloaded, so its version is picked first and the SPTK version follows from it.
// This module does the regrouping, from the JSON of site_downloads.php.

const XMQ_FILE = /xmq/i;

// The version in an XMQ package name: xmq-server_0.9.14_amd64.deb, xmq-server-0.9.14.rpm,
// XMQ-0.9.13.msi, XMQ-0.9.11.2.msi. The fourth component of the Windows installer name is a
// build number, so only the three that make the release are kept.
export function xmqFileVersion(fileName)
{
    if (!XMQ_FILE.test(fileName)) {
        return null;
    }
    const version = fileName.match(/\d+\.\d+\.\d+/);
    return version ? version[0] : null;
}

export function sptkVersionNumber(sptkVersion)
{
    return sptkVersion.replace(/^SPTK-/, "");
}

function compareVersionsDescending(left, right)
{
    const leftParts = left.split(".").map(Number);
    const rightParts = right.split(".").map(Number);
    for (let i = 0; i < Math.max(leftParts.length, rightParts.length); i++) {
        const difference = (rightParts[i] || 0) - (leftParts[i] || 0);
        if (difference !== 0) {
            return difference;
        }
    }
    return 0;
}

// A release directory that the download endpoint could not read comes back as an empty object
function validDirectories(sptkVersion)
{
    if (!sptkVersion || !Array.isArray(sptkVersion.directories)) {
        return [];
    }
    return sptkVersion.directories.filter((entry) => entry && entry.directory && Array.isArray(entry.files));
}

const BINARY_PACKAGE = /\.(deb|rpm|pkg|exe|msi)$/i;

// Whether a directory holds packages for an operating system, as opposed to the source code
// archives that belong to SPTK alone and are listed with every operating system.
//
// Decided by what the directory holds, not by what it is missing. The rule used to be "no XMQ
// package here, so it must be the source code" - and a directory of SPTK .rpm files for which XMQ
// simply had not built, oraclelinux-9 and oraclelinux-10, answered to that description. They were
// then shown under the source-code heading whatever operating system was chosen: pick Ubuntu, and
// below the .deb files came two tables of Oracle Linux .rpm files, offered as though they were the
// source code.
function holdsBinaryPackages(entry)
{
    return entry.files.some((file) => BINARY_PACKAGE.test(file.file));
}

// The list a reader picks from, in the order a reader expects: by name, with numbers read as
// numbers so that Oracle Linux 9 comes before 10 and Ubuntu 24.04 before 26.04.
//
// It used to keep the endpoint's own order - a curated list of known systems, with anything missing
// from it appended afterwards. Every new distribution therefore arrived at the end, in whatever
// order the object happened to hold, and the list read as unsorted: four of them had accumulated
// there by 2026-09-19. Sorting here means the order no longer depends on whether somebody
// remembered to add a name to the endpoint.
//
// The source code goes last whatever it is called. It is not an operating system, and offering it
// between two of them reads as one.
function makeOsList(release)
{
    const osList = [];
    for (const [osDir, entry] of Object.entries(release.osIndex)) {
        osList.push({value: osDir, text: entry.title});
    }
    const isSource = (item) => /source/i.test(item.text);
    return osList.sort((left, right) => {
        if (isSource(left) !== isSource(right)) {
            return isSource(left) ? 1 : -1;
        }
        return left.text.localeCompare(right.text, undefined, {numeric: true, sensitivity: "base"});
    });
}

// Which entry to offer first: the one that matches the visitor's own system, where that can be told.
//
// What a browser actually reports is the family - Windows, macOS, FreeBSD, Android - and for Linux
// only "Linux", never the distribution. So Windows and FreeBSD are chosen outright; a Linux visitor
// is offered the newest Ubuntu, which is a guess, unless the user agent happens to name a family
// (Firefox on Ubuntu has long put "Ubuntu" there, and some builds name Fedora or Debian). A visitor
// on macOS, Android or anything else with no package here is left with the first entry rather than
// being offered something that will not install.
//
// It is only ever the initial selection. The list sits next to it, and a wrong guess costs one click.
// The pages are pre-rendered at build time, where there is no navigator at all - hence the guard
// rather than a plain navigator.userAgent.
export function preferredOs(osList, userAgent = (typeof navigator === "undefined" ? "" : navigator.userAgent || ""))
{
    if (osList.length === 0) {
        return null;
    }
    const lastMatching = (pattern) => {
        const matches = osList.filter((item) => pattern.test(item.text));
        return matches.length > 0 ? matches[matches.length - 1] : null;
    };

    let chosen = null;
    if (/windows|win32|win64/i.test(userAgent)) {
        chosen = lastMatching(/^windows/i);
    } else if (/freebsd/i.test(userAgent)) {
        chosen = lastMatching(/^freebsd/i);
    } else if (/linux/i.test(userAgent) && !/android/i.test(userAgent)) {
        for (const family of [/ubuntu/i, /fedora/i, /debian/i, /oracle/i]) {
            if (family.test(userAgent)) {
                chosen = lastMatching(family);
                break;
            }
        }
        if (!chosen) {
            chosen = lastMatching(/^ubuntu/i);
        }
    }
    return chosen || osList[0];
}

// Groups every download directory by the XMQ version its packages carry. The SPTK versions
// arrive newest first, so the first directory found for an operating system is the one from
// the newest release, and the rest are older copies of the same XMQ packages.
export function buildXmqReleases(downloads)
{
    if (!Array.isArray(downloads)) {
        return {xmqVersions: [], releaseIndex: {}};
    }

    const sptkVersionIndex = {};
    for (const sptkVersion of downloads) {
        sptkVersionIndex[sptkVersion.sptk_version] = sptkVersion;
    }

    const releaseIndex = {};
    for (const sptkVersion of downloads) {
        for (const entry of validDirectories(sptkVersion)) {
            const osDir = entry.directory.os_dir;
            const fileVersions = new Set(entry.files.map((file) => xmqFileVersion(file.file)).filter(Boolean));
            for (const xmqVersion of fileVersions) {
                if (!releaseIndex[xmqVersion]) {
                    // The newest SPTK version holding this XMQ version is the one it is shown with
                    releaseIndex[xmqVersion] = {
                        sptkVersion: sptkVersion.sptk_version, osIndex: {}, osList: [], sourceEntries: []
                    };
                }
                const release = releaseIndex[xmqVersion];
                if (!release.osIndex[osDir]) {
                    release.osIndex[osDir] = {
                        sptkVersion: sptkVersion.sptk_version,
                        osDir: osDir,
                        title: entry.directory.title,
                        files: entry.files
                    };
                }
            }
        }
    }

    for (const release of Object.values(releaseIndex)) {
        const primaryVersion = sptkVersionIndex[release.sptkVersion];
        for (const entry of validDirectories(primaryVersion)) {
            const osDir = entry.directory.os_dir;
            if (!holdsBinaryPackages(entry)) {
                // The source code of the SPTK release, offered whatever the operating system
                release.sourceEntries.push({
                    sptkVersion: release.sptkVersion,
                    osDir: osDir,
                    title: entry.directory.title,
                    files: entry.files
                });
            }
        }
        release.osList = makeOsList(release);
    }

    const xmqVersions = Object.keys(releaseIndex)
        .sort(compareVersionsDescending)
        .map((xmqVersion) => ({value: xmqVersion, text: xmqVersion}));

    return {xmqVersions: xmqVersions, releaseIndex: releaseIndex};
}
